import React, { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Component overrides kept at module scope so React treats the subtree as stable.
const MD_COMPONENTS = {
  p: (props) => <p className="mb-2 last:mb-0 leading-relaxed" {...props} />,
  strong: (props) => (
    <strong className="font-semibold text-slate-900 dark:text-slate-50" {...props} />
  ),
  em: (props) => <em className="italic" {...props} />,
  ul: (props) => <ul className="list-disc pl-5 space-y-1 my-2" {...props} />,
  ol: (props) => <ol className="list-decimal pl-5 space-y-1 my-2" {...props} />,
  li: (props) => <li className="leading-relaxed" {...props} />,
  h1: (props) => (
    <h3 className="text-sm font-bold mt-3 mb-1.5 text-slate-900 dark:text-slate-50" {...props} />
  ),
  h2: (props) => (
    <h3 className="text-sm font-bold mt-3 mb-1.5 text-slate-900 dark:text-slate-50" {...props} />
  ),
  h3: (props) => (
    <h4 className="text-sm font-semibold mt-2.5 mb-1 text-slate-900 dark:text-slate-50" {...props} />
  ),
  h4: (props) => (
    <h4 className="text-sm font-semibold mt-2 mb-1 text-slate-900 dark:text-slate-50" {...props} />
  ),
  hr: () => <hr className="my-3 border-slate-200 dark:border-slate-700" />,
  blockquote: (props) => (
    <blockquote
      className="border-l-2 border-slate-300 dark:border-slate-600 pl-3 italic text-slate-600 dark:text-slate-400 my-2"
      {...props}
    />
  ),
  code: ({ inline, className, children, ...props }) =>
    inline ? (
      <code
        className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-xs font-mono"
        {...props}
      >
        {children}
      </code>
    ) : (
      <pre className="my-2 p-3 rounded-lg bg-slate-900 dark:bg-black text-slate-100 text-xs font-mono overflow-x-auto">
        <code {...props}>{children}</code>
      </pre>
    ),
  a: (props) => (
    <a
      className="text-[#1565C0] dark:text-[#65D95A] underline underline-offset-2 hover:opacity-80"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  table: (props) => (
    <div className="my-3 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
      <table className="w-full text-xs border-collapse" {...props} />
    </div>
  ),
  thead: (props) => <thead className="bg-slate-100 dark:bg-slate-800" {...props} />,
  tbody: (props) => <tbody {...props} />,
  tr: (props) => (
    <tr className="border-b border-slate-200 dark:border-slate-700 last:border-0" {...props} />
  ),
  th: (props) => (
    <th
      className="px-2.5 py-1.5 text-left font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap"
      {...props}
    />
  ),
  td: (props) => (
    <td className="px-2.5 py-1.5 text-slate-700 dark:text-slate-200 align-top" {...props} />
  ),
};

/**
 * Renders assistant Markdown output with RendPleX-branded styling.
 * Supports GitHub-flavored Markdown (tables, task lists, strikethrough).
 * Memoized so completed messages don't re-render while a new one streams.
 */
function MarkdownContent({ children }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
      {children}
    </ReactMarkdown>
  );
}

export default memo(MarkdownContent);
